import React from 'react';
import { Box, Typography } from '@mui/material';
import CertificateHeader from './CertificateHeader';


/**
 * Bonafide Certificate - Detailed Template with student info and photo
 * Matches ePrashasan reference image (filled format with photo placeholder)
 */
export const BonafideTemplateDetailed = ({ data }) => {
    const { school, student, details, issueDate, academicYear, division } = data;

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return `${match[3]}/${match[2]}/${match[1]}`;
        }
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        } catch (e) {
            return dateStr;
        }
    };

    const formatDateInWords = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
            'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
            'Seventeen', 'Eighteen', 'Nineteen'];
        const tens = ['', '', 'Twenty', 'Thirty'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();

        const dayWord = day < 20 ? ones[day] : tens[Math.floor(day / 10)] + (day % 10 ? ' ' + ones[day % 10] : '');
        const yearSuffix = year - 2000;
        const yearWord = yearSuffix < 20 ? ones[yearSuffix] : tens[Math.floor(yearSuffix / 10)] + (yearSuffix % 10 ? ' ' + ones[yearSuffix % 10] : '');

        return `(${dayWord} ${month} Two Thousand ${yearWord})`;
    };

    return (
        <Box sx={{
            fontFamily: '"Times New Roman", Georgia, serif',
            fontSize: '13px',
            lineHeight: 1.6,
            color: '#000',
            backgroundColor: '#fff',
            p: '15mm',
            width: '210mm',
            minHeight: '297mm',
            boxSizing: 'border-box',
            border: '1px solid #999',
            position: 'relative',
            '@media print': {
                width: '210mm',
                margin: 0,
                padding: '15mm',
                border: 'none',
            }
        }}>
            {/* School Header */}
            <CertificateHeader isLeavingCertificate={false} details={details} school={school} />


            {/* Certificate Title */}
            <Typography sx={{
                textAlign: 'center', fontSize: 16, fontWeight: 'bold',
                textDecoration: 'underline', mb: 2, fontFamily: 'inherit'
            }}>
                Bonafide Certificate
            </Typography>

            {/* Reg No & Academic Year row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, px: 1 }}>
                <Typography sx={{ fontSize: 13, fontFamily: 'inherit' }}>
                    <strong>Reg No: {student?.admissionNumber || '___'}</strong> ✓
                </Typography>
                <Typography sx={{ fontSize: 13, fontFamily: 'inherit' }}>
                    <strong>Academic Year: {academicYear || '2025-2026'}</strong> ✓
                </Typography>
                <Typography sx={{ fontSize: 13, fontFamily: 'inherit', color: '#e53935' }}>
                    <strong>Sr. No. : {details?.srNumber ? String(details.srNumber).padStart(5, '0') : '00001'}</strong>
                </Typography>
            </Box>

            {/* Certificate Body */}
            <Box sx={{ px: 1, mb: 2 }}>
                <Typography sx={{ fontSize: 13, mb: 0.5, fontFamily: 'inherit' }}>
                    <strong>This is to certify that,</strong>
                </Typography>
                <Typography sx={{ fontSize: 13, mb: 2, fontFamily: 'inherit' }}>
                    <strong>The details of Bonafide Student of this School/College are given below.</strong>
                </Typography>
            </Box>

            {/* Details with Photo */}
            <Box sx={{ display: 'flex', gap: 3, px: 1, mb: 2 }}>
                {/* Details */}
                <Box sx={{ flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <DetailRow label="Student Full Name" value={student?.name} editable />
                            <DetailRow label="Mother Name" value={student?.motherName || details?.motherName} editable />
                            <DetailRow label="Standard & Division" value={`${details?.currentClass || ''} ${division || details?.currentDivision || ''}`} editable />
                            <DetailRow label="Birth Date(In Figure)" value={formatDate(details?.dateOfBirth)} editable />
                            <DetailRow label="Birth Date(In Words)" value={details?.dateOfBirthInWords || formatDateInWords(details?.dateOfBirth)} />
                            <DetailRow label="Place Of Birth" value={details?.placeOfBirth} editable />
                            <DetailRow label="Aadhar Card No" value={details?.uid} editable />
                            <DetailRow label="Caste" value={details?.caste} editable />
                        </tbody>
                    </table>
                </Box>
                {/* Photo */}
                <Box sx={{
                    width: 100, height: 120, border: '1px solid #000',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, flexShrink: 0, mt: 1
                }}>
                    Photo
                </Box>
            </Box>

            {/* Character Statement */}
            <Typography sx={{ fontSize: 13, mt: 3, mb: 2, px: 1, fontWeight: 'bold', fontFamily: 'inherit' }}>
                To the best of my knowledge he/she bears a good moral characters.
            </Typography>

            {/* Signature Section */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                mt: 6, px: 1, fontFamily: 'inherit'
            }}>
                <Box>
                    <Typography sx={{ fontSize: 12 }}>Date : {issueDate ? formatDate(issueDate) : formatDate(new Date())}</Typography>
                    <Typography sx={{ fontSize: 12 }}>Place :</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 13 }}>Clerk</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 13 }}>Teacher</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', pr: '55px' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 'bold' }}>Principal</Typography>
                    <Typography sx={{ fontSize: 11 }}>Vidya Pratishthan's</Typography>
                    <Typography sx={{ fontSize: 11 }}>Supe English Medium School</Typography>
                </Box>
            </Box>
        </Box>
    );
};

// Helper component for detail rows
const DetailRow = ({ label, value, editable }) => (
    <tr>
        <td style={{
            padding: '4px 0', fontSize: 13, fontWeight: 'bold',
            width: '40%', verticalAlign: 'top'
        }}>
            {label}
        </td>
        <td style={{ padding: '4px 8px', fontSize: 13, width: 20 }}>:</td>
        <td style={{
            padding: '4px 0', fontSize: 13,
            color: value ? '#1565c0' : '#999',
            fontWeight: value ? 600 : 400
        }}>
            {value || '-'}
            {editable && value && <span style={{ color: '#4caf50', marginLeft: 4, fontSize: 11 }}>✓</span>}
        </td>
    </tr>
);

export default BonafideTemplateDetailed;
